<template>
    <div v-if="auction.winner" class="auction-detail__info-body">
        <div class="auction-detail-bid">
            <div
                v-if="auction.is_user_lot || isAdmin()"
                class="auction-detail-bid__block auction-detail-bid__block-winner"
            >
                <div class="auction-detail-bid__block-label">Победитель</div>
                <div class="auction-detail-bid__block-value">
                    {{ auction.winner.dealer_name }}
                </div>
            </div>

            <div
                v-if="!isAdmin() && !auction.is_user_lot"
                class="auction-detail-bid__block"
                :class="{ 'auction-detail-bid__block-user-bid': auctionLastUserBidValue }"
            >
                <div class="auction-detail-bid__block-label">Ваша ставка</div>
                <div class="auction-detail-bid__block-value">
                    {{ auctionLastUserBidValue ? numberFormat(auctionLastUserBidValue) + ' ₽' : '—' }}
                </div>
            </div>

            <div class="auction-detail-bid__block">
                <div class="auction-detail-bid__block-label">Ставка победителя</div>
                <div class="auction-detail-bid__block-value">
                    {{ numberFormat(auction.winner.value) }} ₽
                </div>
            </div>

            <ViewAuctionInfoBidsCount
                :auction="auction"
                :opened="showBids"
                @toggle-bids="showBids = !showBids"
            />

            <div class="auction-detail-bid__block auction-detail-bid__actions">
                <UISimpleButton
                    v-if="canConfirmAuctionWinner(auction)"
                    class="auction-detail-bid__action"
                    @clicked="handleConfirmWinner"
                >
                    Подтвердить результаты
                </UISimpleButton>

                <div class="auction-detail-bid__documents">
                    <AuctionSetDeliveryRequired
                        v-if="canSetDeliveryRequired(auction)"
                        :auction-id="auction.id"
                        :is-delivery-required="auction.delivery_required ?? false"
                    />

                    <UIDropDown
                        v-if="canViewAuctionSaleContract(auction) || canViewAuctionInvoice(auction)"
                        class="auction-detail-bid__action"
                    >
                        <template #trigger>
                            <UISimpleButton>
                                <template #icon>
                                    <img v-svg-inline src="@/assets/icons/report.svg" />
                                </template>

                                <template #icon-right>
                                    <img v-svg-inline src="@/assets/icons/ui/select-arrow.svg" />
                                </template>
                                Документы
                            </UISimpleButton>
                        </template>

                        <template #elements>
                            <li
                                v-if="canViewAuctionSaleContract(auction)"
                                class="ui-dropdown-menu__element"
                                @click="onDownloadSaleContract"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/document.svg" />
                                <span>ДКП</span>
                            </li>

                            <li
                                v-if="canViewAuctionInvoice(auction)"
                                class="ui-dropdown-menu__element"
                                @click="onDownloadInvoice"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/document.svg" />
                                <span>Счет на оплату</span>
                            </li>
                        </template>
                    </UIDropDown>
                </div>
            </div>
        </div>

        <ViewAuctionBidsList :auction="auction" :showed="showBids" />
    </div>
</template>

<script lang="ts" setup>
    import { computed, defineEmits, PropType, ref } from 'vue';

    import { injectStrict } from '@/injection/injectStrict.ts';
    import { ModalKey } from '@/injection/keys.ts';

    import { Auction } from '@/types/auction.type.ts';

    import useDocumentDownloader from '@/utils/useDocumentDownloader.ts';
    import usePermission from '@/repositories/auth/usePermission.ts';

    import { numberFormat } from '@/utils/NumberFormatter.ts';

    import UIDropDown from '@/components/ui/UIDropDown/UIDropDown.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import AuctionConfirmWinner from '@/components/auction/AuctionConfirmWinner/AuctionConfirmWinner.vue';
    import AuctionSetDeliveryRequired from '@/components/auction/AuctionSetDeliveryRequired/AuctionSetDeliveryRequired.vue';

    import ViewAuctionBidsList from '@/views/auctions/ViewAuction/components/ViewAuctionBidsList/ViewAuctionBidsList.vue';
    import ViewAuctionInfoBidsCount from '@/views/auctions/ViewAuction/components/ViewAuctionInfoBidsCount/ViewAuctionInfoBidsCount.vue';

    const { auction } = defineProps({
        auction: {
            type: Object as PropType<Auction>,
            required: true,
        },
    });

    const emit = defineEmits<{
        (e: 'confirmed-winner'): void,
    }>();

    const $modal = injectStrict(ModalKey);

    const { isAdmin, canConfirmAuctionWinner, canViewAuctionSaleContract, canViewAuctionInvoice, canSetDeliveryRequired } = usePermission();
    const { downloadAuctionSaleContract, downloadAuctionInvoice } = useDocumentDownloader();

    const showBids = ref<boolean>(false);

    const auctionLastUserBidValue = computed(() => {
        return auction.user_bids && auction.user_bids.length > 0
            ? auction.user_bids[auction.user_bids.length - 1].value
            : undefined;
    });

    function handleConfirmWinner(): void {
        $modal?.open({
            component: AuctionConfirmWinner,
            props: {
                auctionId: auction.id,
                winner: auction.winner,
                onConfirmed: () => {
                    $modal?.close();
                    emit('confirmed-winner');
                },
            },
            modalType: 'narrow',
        });
    }

    function onDownloadSaleContract(): void {
        if (auction.id) {
            downloadAuctionSaleContract(auction.id, `auction-${auction.id}-sale-contract.docx`);
        }
    }

    function onDownloadInvoice(): void {
        if (auction.id) {
            downloadAuctionInvoice(auction.id, `auction-${auction.id}-invoice.xlsx`);
        }
    }
</script>

<style lang="scss" scoped>
    @use "ViewAuctionInfoResults.scss";
</style>
